import { ExploreTrendsStackKpiDataDto } from '@data-visualization/trends-stack-chart/Types';
import { ColumnType, DataCell, DataCellValue, TableColumn } from '@ecosystem/export-api';
import { GRAND_TOTAL } from '@utils/TotalUtils';

export const getTrendStackChartExportColumn = (categories: string[], showPerformanceWithin: string): TableColumn[] => {
  const periodColumns = categories.map((category) => {
    return {
      header: category,
    };
  });
  return [{ header: showPerformanceWithin }, { header: 'KPI' }, ...periodColumns];
};

export const getTrendStackChartExportRow = (series: ExploreTrendsStackKpiDataDto[]): DataCell[][] => {
  return series
    .map((data: ExploreTrendsStackKpiDataDto) => ({
      kpiTypeOrder: KpiTypeOrder[KpiTypeMapper[data.kpiType]],
      dataArr: data.kpiData.map((kpi) => [
        { value: kpi.name === GRAND_TOTAL ? 'Total' : kpi.name, type: ColumnType.Text },
        { value: KpiTypeMapper[data.kpiType], type: ColumnType.Text },
        ...(kpi.data?.map((d) => mapKpiValueToDataCell(d, KpiTypeMapper[data.kpiType])) || []),
      ]),
    }))
    .sort(compareExportPerformanceKpiByKpiType)
    .flatMap((kpi) => kpi.dataArr);
};

type KpiOrderAndDataList = {
  kpiTypeOrder: number;
  dataArr: DataCell[][];
};

const compareExportPerformanceKpiByKpiType = (kpi1: KpiOrderAndDataList, kpi2: KpiOrderAndDataList): number => {
  return kpi1.kpiTypeOrder - kpi2.kpiTypeOrder;
};

enum KpiTypeName {
  MarketRevenueAbsolute = 'Market Revenue',
  MarketRevenueShare = 'Market Revenue Share',
  MarketUnitAbsolute = 'Market Units',
  MarketUnitShare = 'Market Units Share',
  RetailerRevenueAbsolute = 'Retailer Revenue',
  RetailerRevenueShare = 'Retailer Revenue Share',
  RetailerUnitAbsolute = 'Retailer Units',
  RetailerUnitShare = 'Retailer Units Share',
}

const KpiTypeMapper = {
  marketRevenueAbsolute: KpiTypeName.MarketRevenueAbsolute,
  marketRevenueShare: KpiTypeName.MarketRevenueShare,
  marketUnitAbsolute: KpiTypeName.MarketUnitAbsolute,
  marketUnitShare: KpiTypeName.MarketUnitShare,
  retailerRevenueAbsolute: KpiTypeName.RetailerRevenueAbsolute,
  retailerRevenueShare: KpiTypeName.RetailerRevenueShare,
  retailerUnitAbsolute: KpiTypeName.RetailerUnitAbsolute,
  retailerUnitShare: KpiTypeName.RetailerUnitShare,
};

const KpiTypeOrder: { [key in KpiTypeName]: number } = {
  [KpiTypeName.MarketRevenueAbsolute]: 1,
  [KpiTypeName.MarketRevenueShare]: 2,
  [KpiTypeName.MarketUnitAbsolute]: 3,
  [KpiTypeName.MarketUnitShare]: 4,
  [KpiTypeName.RetailerRevenueAbsolute]: 5,
  [KpiTypeName.RetailerRevenueShare]: 6,
  [KpiTypeName.RetailerUnitAbsolute]: 7,
  [KpiTypeName.RetailerUnitShare]: 8,
};

const mapKpiValueToDataCell = (value: DataCellValue, kpiType: string): DataCell => {
  switch (kpiType) {
    case KpiTypeName.RetailerRevenueAbsolute:
    case KpiTypeName.MarketRevenueAbsolute:
      return { value: value, type: ColumnType.Currency };
    case KpiTypeName.RetailerUnitAbsolute:
    case KpiTypeName.MarketUnitAbsolute:
      return { value: value, type: ColumnType.Number };
    case KpiTypeName.RetailerRevenueShare:
    case KpiTypeName.RetailerUnitShare:
    case KpiTypeName.MarketRevenueShare:
    case KpiTypeName.MarketUnitShare:
      return { value: (value as number) / 100, type: ColumnType.Percentage };
    default:
      throw new Error(`Unknown KPI type: ${kpiType}`);
  }
};
